import { useEffect, useState } from "react";
import type { ProjectBrief } from "../../contracts/types";
import { getNamedMap, mergeNamedMap, listProjects, loadNamed } from "../projectStore";
import { getMe, getSyncedContracts } from "../authApi";
import { SAMPLE_PROJECT } from "../sampleProject";

/* ============================================================================
   ?pick=1 — contract picker for sibling apps (Atelier Project Studio's Bid
   Manager). Opened as a popup; the user chooses a saved contract (or the
   sample) and we postMessage the full ProjectBrief back to the opener, which
   imports it as the tender's draft contract. Nothing is written here.
   ========================================================================== */

const ALLOWED_OPENERS = [
  "https://studio.studiogy.design",
  "https://atelier-studio.renauto.workers.dev",
  "http://localhost:4347",
];

function send(name: string, brief: ProjectBrief) {
  if (!window.opener) return;
  for (const origin of ALLOWED_OPENERS) {
    try { (window.opener as Window).postMessage({ type: "cg-contract-pick", name, brief }, origin); } catch { /* origin mismatch — dropped */ }
  }
  window.close();
}

export function ContractPicker() {
  const [items, setItems] = useState(() => listProjects());
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Choose a contract — Studio Contract";
    void (async () => {
      const me = await getMe();
      setEmail(me.email);
      if (me.email) {
        const synced = await getSyncedContracts();
        if (synced.data) { mergeNamedMap(synced.data as never); setItems(listProjects()); }
      }
      setLoading(false);
    })();
  }, []);

  function pick(name: string) {
    const brief = loadNamed(name) ?? getNamedMap()[name]?.brief;
    if (brief) send(name, brief);
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--paper, #faf8f2)", padding: "36px 26px", fontFamily: "inherit" }}>
      <div style={{ maxWidth: 460, margin: "0 auto" }}>
        <div style={{ fontFamily: "Georgia, serif", fontSize: 24 }}>Studio · Contract</div>
        <div style={{ fontSize: 11, letterSpacing: 2.4, textTransform: "uppercase", opacity: 0.55, marginTop: 4 }}>
          Choose a contract for the tender
        </div>
        <p style={{ fontSize: 13, opacity: 0.7, margin: "14px 0 20px", lineHeight: 1.55 }}>
          The selected contract is handed to the Bid Manager as the tender's draft — bidders see its particulars
          read-only for reference. Revisions stay here.
        </p>

        {!window.opener && (
          <div style={{ padding: "12px 14px", border: "1px solid #d9d4c5", borderRadius: 10, fontSize: 13, marginBottom: 16 }}>
            This picker is meant to be opened from the Bid Manager's Draft Contract page.
          </div>
        )}

        <div style={{ display: "grid", gap: 8 }}>
          {items.map((p) => (
            <button key={p.name} onClick={() => pick(p.name)}
              style={{ textAlign: "left", padding: "13px 16px", border: "1px solid #d9d4c5", borderRadius: 12, background: "#fff", cursor: "pointer", fontSize: 14 }}>
              <span style={{ fontWeight: 600 }}>{p.name}</span>
              <span style={{ display: "block", fontSize: 11.5, opacity: 0.55, marginTop: 3 }}>
                saved {p.savedAt ? p.savedAt.slice(0, 10) : "—"}
              </span>
            </button>
          ))}
          {!loading && items.length === 0 && (
            <div style={{ padding: "12px 14px", fontSize: 13, opacity: 0.65 }}>
              No saved contracts yet{email ? "" : " on this device — sign in to the studio to load your account's contracts"}.
            </div>
          )}
          <button onClick={() => send("Sample — FIDIC 2017 reference", SAMPLE_PROJECT)}
            style={{ textAlign: "left", padding: "13px 16px", border: "1px dashed #c9c4b5", borderRadius: 12, background: "transparent", cursor: "pointer", fontSize: 14 }}>
            <span style={{ fontWeight: 600 }}>Sample contract — FIDIC 2017</span>
            <span style={{ display: "block", fontSize: 11.5, opacity: 0.55, marginTop: 3 }}>
              a complete reference draft to show bidders the intended form
            </span>
          </button>
        </div>

        <div style={{ fontSize: 11.5, opacity: 0.5, marginTop: 18 }}>
          {loading ? "Checking your studio account…" : email ? `Signed in as ${email} — account contracts included.` : "Not signed in — showing this device's contracts only."}
        </div>
      </div>
    </div>
  );
}
